import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CalendarClock, Save } from 'lucide-react'

import { ApiError } from '@/api/client'
import { instrumentsApi, teachersApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { isOwner, isTeacher } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { MultiSelect, TextField } from '@/components/ui/Field'
import { InstrumentBadges, StudentStatusBadge } from '@/components/ui/InstrumentBadge'
import { FullScreenLoader } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { AvailabilityEditor } from './AvailabilityEditor'

export function TeacherDetailPage() {
  const { teacherId = '' } = useParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess } = useToast()

  const [bio, setBio] = useState('')
  const [certificates, setCertificates] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [instrumentIds, setInstrumentIds] = useState<string[]>([])
  const [accepting, setAccepting] = useState(true)
  const [substitutions, setSubstitutions] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const profileQuery = useQuery({
    queryKey: ['teachers', teacherId],
    queryFn: () => teachersApi.get(teacherId),
    enabled: teacherId !== '',
  })

  const studentsQuery = useQuery({
    queryKey: ['teachers', teacherId, 'students'],
    queryFn: () => teachersApi.students(teacherId),
    enabled: teacherId !== '',
  })

  const instrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.list(),
  })

  const profile = profileQuery.data

  useEffect(() => {
    if (!profile) return
    setBio(profile.bio ?? '')
    setCertificates(profile.certificates ?? '')
    setHourlyRate(profile.hourly_rate ?? '')
    setInstrumentIds(profile.instruments.map((instrument) => instrument.id))
    setAccepting(profile.is_accepting_students)
    setSubstitutions(profile.accepts_substitutions)
  }, [profile])

  const isSelf = user?.id === teacherId
  const canEdit = isSelf || isOwner(user) || user?.role === 'ADMIN'
  // Stawkę godzinową ustala administracja — nauczyciel widzi ją tylko u siebie.
  const canEditRate = canEdit && !isTeacher(user)
  const canSeeRate = canEditRate || isSelf

  const save = useMutation({
    mutationFn: () =>
      teachersApi.update(teacherId, {
        bio: bio.trim() || null,
        certificates: certificates.trim() || null,
        instrument_ids: instrumentIds,
        is_accepting_students: accepting,
        accepts_substitutions: substitutions,
        // Pole stawki wysyłamy tylko gdy użytkownik ma prawo je zmieniać —
        // inaczej backend odrzuci całe żądanie.
        ...(canEditRate ? { hourly_rate: hourlyRate.trim() || null } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teachers'] })
      notifySuccess('Profil został zapisany.')
      setError(null)
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się zapisać profilu.')
    },
  })

  if (profileQuery.isPending) return <FullScreenLoader label="Wczytywanie profilu…" />

  if (profileQuery.isError || !profile) {
    const isNotFound = profileQuery.error instanceof ApiError && profileQuery.error.isNotFound
    return (
      <Alert tone="error" title={isNotFound ? 'Nie znaleziono nauczyciela' : 'Błąd wczytywania'}>
        {isNotFound
          ? 'Nauczyciel nie istnieje albo nie masz do niego dostępu.'
          : 'Spróbuj odświeżyć stronę.'}{' '}
        <Link
          to={isTeacher(user) ? '/ustawienia' : '/nauczyciele'}
          className="font-bold text-primary underline"
        >
          {isTeacher(user) ? 'Wróć do ustawień' : 'Wróć do listy'}
        </Link>
      </Alert>
    )
  }

  const students = studentsQuery.data ?? []

  return (
    <>
      {/* Nauczyciel nie ma dostępu do listy kadry — wraca do Ustawień. */}
      <Link
        to={isTeacher(user) ? '/ustawienia' : '/nauczyciele'}
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-on-surface-variant transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {isTeacher(user) ? 'Wróć do ustawień' : 'Wróć do listy nauczycieli'}
      </Link>

      <PageHeader
        title={profile.user.full_name}
        description={profile.user.email}
        breadcrumb={['Szkoła', 'Nauczyciele', profile.user.full_name]}
      />

      {/* `[&>*]:min-w-0` nie jest ozdobą: element siatki domyślnie nie schodzi
          poniżej szerokości swojej treści, więc na wąskim ekranie kolumna
          rozpychała stronę zamiast się zwęzić. Zmierzone przy 320 px:
          354 px zamiast 320, czyli można było wyjechać poza obrys. */}
      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Profil zawodowy"
              action={
                canEdit && (
                  <Button
                    size="sm"
                    icon={<Save size={16} />}
                    isLoading={save.isPending}
                    onClick={() => save.mutate()}
                  >
                    Zapisz
                  </Button>
                )
              }
            />
            <div className="space-y-5 p-6">
              {error && <Alert tone="error">{error}</Alert>}
              {!canEdit && (
                <Alert tone="info">Masz podgląd profilu — edycja jest niedostępna.</Alert>
              )}

              <div className="flex items-center gap-4">
                <Avatar name={profile.user.full_name} size="lg" />
                <div className="min-w-0">
                  {profile.instruments.length === 0 ? (
                    <span className="text-sm text-on-surface-variant">
                      Nie wybrano instrumentów
                    </span>
                  ) : (
                    <InstrumentBadges instruments={profile.instruments} />
                  )}
                </div>
              </div>

              <MultiSelect
                label="Instrumenty, których uczy"
                options={(instrumentsQuery.data ?? []).map((instrument) => ({
                  value: instrument.id,
                  label: instrument.name,
                }))}
                selected={instrumentIds}
                onChange={setInstrumentIds}
                disabled={!canEdit}
                placeholder="Wybierz instrumenty"
                emptyMessage="Brak instrumentów w słowniku."
              />

              <div className="space-y-1.5">
                <label htmlFor="bio" className="block text-sm font-semibold">
                  Biogram
                </label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(event) => setBio(event.target.value)}
                  rows={4}
                  maxLength={4000}
                  disabled={!canEdit}
                  placeholder="Wykształcenie, doświadczenie, podejście do nauczania…"
                  className="w-full rounded-lg border-2 border-transparent bg-surface-container-low px-4 py-3 text-sm transition-colors placeholder:text-on-surface-variant/50 focus:border-primary focus:bg-surface focus:outline-none disabled:opacity-60"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="certificates" className="block text-sm font-semibold">
                  Certyfikaty i dyplomy
                </label>
                <textarea
                  id="certificates"
                  value={certificates}
                  onChange={(event) => setCertificates(event.target.value)}
                  rows={3}
                  maxLength={4000}
                  disabled={!canEdit}
                  className="w-full rounded-lg border-2 border-transparent bg-surface-container-low px-4 py-3 text-sm transition-colors focus:border-primary focus:bg-surface focus:outline-none disabled:opacity-60"
                />
              </div>

              {canSeeRate && (
                <TextField
                  label="Stawka godzinowa (zł)"
                  type="number"
                  min={0}
                  step="0.01"
                  value={hourlyRate}
                  onChange={(event) => setHourlyRate(event.target.value)}
                  disabled={!canEditRate}
                  hint={
                    canEditRate
                      ? 'Podstawa naliczania w raporcie do wypłat.'
                      : 'Stawkę ustala administracja szkoły.'
                  }
                />
              )}

              <div className="flex flex-wrap gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-surface-container-low px-4 py-2.5 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={accepting}
                    onChange={(event) => setAccepting(event.target.checked)}
                    disabled={!canEdit}
                    className="h-4 w-4 rounded border-outline/40 text-primary focus:ring-primary"
                  />
                  Przyjmuje nowych uczniów
                </label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-surface-container-low px-4 py-2.5 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={substitutions}
                    onChange={(event) => setSubstitutions(event.target.checked)}
                    disabled={!canEdit}
                    className="h-4 w-4 rounded border-outline/40 text-primary focus:ring-primary"
                  />
                  Dostępny do zastępstw
                </label>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Dostępność tygodniowa" />
            <AvailabilityEditor
              teacherId={teacherId}
              slots={profile.availability}
              locations={profile.locations}
              canEdit={canEdit}
            />
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title={`Uczniowie (${students.length})`} />
            {students.length === 0 ? (
              <EmptyState
                icon={<CalendarClock size={24} />}
                title="Brak przypisanych uczniów"
                description="Uczniów przypisuje się na karcie ucznia w sekcji „Nauka”."
              />
            ) : (
              <ul className="divide-y divide-outline/10">
                {students.map((student) => (
                  <li key={student.id}>
                    <Link
                      to={`/uczniowie/${student.id}`}
                      className="flex items-center gap-3 px-6 py-4 transition-colors hover:bg-surface-container-low"
                    >
                      <Avatar name={student.full_name} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-bold text-on-surface">{student.full_name}</p>
                        <p className="text-xs text-on-surface-variant">
                          {student.instruments.length > 0
                            ? student.instruments.map((item) => item.name).join(', ')
                            : 'Bez instrumentu'}
                        </p>
                      </div>
                      <StudentStatusBadge status={student.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="Podsumowanie" />
            <div className="space-y-3 p-6 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-on-surface-variant">Instrumenty</span>
                <span className="font-semibold">{profile.instruments.length}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-on-surface-variant">Okna dostępności</span>
                <span className="font-semibold">{profile.availability.length}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-on-surface-variant">Zastępstwa</span>
                <Badge tone={profile.accepts_substitutions ? 'success' : 'neutral'}>
                  {profile.accepts_substitutions ? 'Tak' : 'Nie'}
                </Badge>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}
